import { useMapped } from "@omni/utils/context/mapped.context";
import { cn } from "@omni/utils/styles";
import { ReactNode, useEffect, useRef } from "react";

import { Col } from "@/components/General/Grid/col.component";
import { CategoryContextProvider } from "@/components/Navigation/FlyoutMenu/components/category/context/category.context";
import {
    useCategory,
    useFlyoutMenuContext
} from "@/components/Navigation/FlyoutMenu/context/flyoutMenu.context";
import { useActivationDelay } from "@/components/Navigation/FlyoutMenu/hooks/useActivationDelay.hook";
import { getTagZoneIdFor } from "@/utils/tagging/tag.utils";

type Props = {
    className?: string;
    children: ReactNode;
};

export const Category = ({ className, children }: Props) => {
    const { setActiveCatetoryIndexes } = useFlyoutMenuContext();
    const { index } = useMapped();
    const { category } = useCategory(index);
    const { getActivationDelay } = useActivationDelay();

    const menuElementRef = useRef<HTMLDivElement | null>(null);
    const mouseEnterTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

    useEffect(() => {
        return () => clearTimeout(mouseEnterTimeoutRef.current);
    }, []);

    const onMouseEnter = (subCategoryIndex: number) => {
        const menuElement = menuElementRef.current;
        if (!menuElement) return;

        clearTimeout(mouseEnterTimeoutRef.current);

        const activationDelay = getActivationDelay(menuElement, index);

        mouseEnterTimeoutRef.current = setTimeout(() => {
            setActiveCatetoryIndexes((prev) => [
                ...prev.slice(0, index),
                subCategoryIndex,
                ...prev.slice(index + 1).fill(null)
            ]);
        }, activationDelay ?? 0);
    };

    const onMouseLeave = () => {
        clearTimeout(mouseEnterTimeoutRef.current);
    };

    return (
        <CategoryContextProvider
            index={index}
            category={category}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <Col
                xs={3}
                ref={menuElementRef}
                className={cn(
                    "tw:h-full tw:overflow-auto tw:text-primary tw:border-r tw:border-grey tw:last:border-white!",
                    className
                )}
                {...getTagZoneIdFor(`flyout-menu-category-${index}`)}
            >
                {children}
            </Col>
        </CategoryContextProvider>
    );
};
